import type {
  ByoErrorReason,
  ByoEventIdentity,
  ByoOfferType,
  ByoSlotProp,
  ByoWidgetProps,
} from '@sunflower-games/analytics-events';

export const BYO_PICKS_INVALID_CODE = 3129;

export type ByoOption = {
  optionId: string;
  label?: string | null;
  rewards: { gcCoin: number; scCoin: number; bundleItems: { bundleType: string; bundlePayload: unknown }[] };
};

export type ByoPickSource = 'manual' | 'pick_for_me';

export type ByoSlotConfig = {
  slotIndex: number;
  pickCount?: number | null;
  options: ByoOption[];
};

export type ByoPackage = {
  packageId: number;
  slots: ByoSlotConfig[];
  picks: { position: number; optionId: string; source?: ByoPickSource }[];
  claimed?: { position: number; optionId: string }[];
};

export type ByoBenefit = { cycle: number; packages: ByoPackage[] };

export type ByoPickState = Map<number, Record<number, string>>;
export type ByoPickedForMe = Map<number, Set<string>>;
export type ByoPickToSend = { packageId: number; position: number; optionId: string; source: ByoPickSource };
export type ByoInvalid = { packageId: number; position: number; reason: string };
export type ByoRejection = { invalid: ByoInvalid[]; byo: ByoBenefit };

export const gridSlot = (pkg?: ByoPackage) => pkg?.slots.find((s) => s.pickCount != null);

export const gridPicks = (picks: Record<number, string> = {}) =>
  Object.entries(picks)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, optionId]) => optionId);

// A saved pick whose option is no longer served never counts toward K.
export const servedGridPicks = (slot: ByoSlotConfig, picks?: Record<number, string>) =>
  gridPicks(picks).filter((id) => slot.options.some((o) => o.optionId === id));

export const toPositions = (optionIds: string[]): Record<number, string> =>
  Object.fromEntries(optionIds.map((optionId, position) => [position, optionId]));

export function firstEmptySlot(
  byo: ByoBenefit,
  picks: ByoPickState,
  packageIds: number[],
): { packageId: number; slotIndex: number; grid?: true } | null {
  for (const packageId of packageIds) {
    const pkg = byo.packages.find((p) => p.packageId === packageId);
    const grid = gridSlot(pkg);
    if (grid) {
      const picked = servedGridPicks(grid, picks.get(packageId)).length;
      if (picked < grid.pickCount!) return { packageId, slotIndex: picked, grid: true };
      continue;
    }
    const slot = pkg?.slots.find((s) => picks.get(packageId)?.[s.slotIndex] === undefined);
    if (slot) return { packageId, slotIndex: slot.slotIndex };
  }
  return null;
}

export const byoPackageIds = (byo: ByoBenefit) => byo.packages.map((p) => p.packageId);

export const byoIncomplete = (byo: ByoBenefit | undefined, picks: ByoPickState) =>
  !!byo && firstEmptySlot(byo, picks, byoPackageIds(byo)) !== null;

export function picksForPurchase(
  byo: ByoBenefit,
  picks: ByoPickState,
  packageIds: number[],
  pickedForMe: ByoPickedForMe = new Map(),
): ByoPickToSend[] {
  return packageIds.flatMap((packageId) => {
    const pkg = byo.packages.find((p) => p.packageId === packageId);
    const source = (optionId: string): ByoPickSource =>
      pickedForMe.get(packageId)?.has(optionId) ? 'pick_for_me' : 'manual';
    const grid = gridSlot(pkg);
    if (grid) {
      return servedGridPicks(grid, picks.get(packageId)).map((optionId, position) => ({
        packageId,
        position,
        optionId,
        source: source(optionId),
      }));
    }
    return (pkg?.slots ?? []).flatMap((slot) => {
      const picked = picks.get(packageId)?.[slot.slotIndex];
      return picked === undefined
        ? []
        : [{ packageId, position: slot.slotIndex, optionId: picked, source: source(picked) }];
    });
  });
}

const to2dp = (n: number) => Math.round(n * 100) / 100;

export function pickedByoRewards(
  byo: ByoBenefit | undefined,
  picks: { packageId: number; optionId: string }[] = [],
): ByoOption['rewards'] | null {
  const rewards = picks.flatMap(({ packageId, optionId }) => {
    const option = byo?.packages
      .find((p) => p.packageId === packageId)
      ?.slots.flatMap((slot) => slot.options)
      .find((o) => o.optionId === optionId);
    return option ? [option.rewards] : [];
  });
  if (!rewards.length) return null;
  return {
    gcCoin: rewards.reduce((sum, r) => sum + r.gcCoin, 0),
    scCoin: to2dp(rewards.reduce((sum, r) => sum + r.scCoin, 0)),
    bundleItems: rewards.flatMap((r) => r.bundleItems),
  };
}

type OrderDataWithByo = {
  gcAmount: number;
  scAmount: number;
  packageBundles?: unknown[];
  bundleItems?: unknown[];
  eventTriggerData?: {
    activeEvent?: { benefit?: { byo?: ByoBenefit }; byoPicks?: { packageId: number; optionId: string }[] };
  };
};

export function withPickedByoRewards<T extends OrderDataWithByo>(data: T): T {
  const activeEvent = data.eventTriggerData?.activeEvent;
  const picked = pickedByoRewards(activeEvent?.benefit?.byo, activeEvent?.byoPicks);
  if (!picked) return data;
  // The success screen dedupes the two lists by id; served options carry none.
  const items = picked.bundleItems.map((item, i) => ({ id: `byo-pick-${i}`, ...item }));
  return {
    ...data,
    gcAmount: data.gcAmount + picked.gcCoin,
    scAmount: to2dp(data.scAmount + picked.scCoin),
    ...(data.packageBundles && { packageBundles: [...data.packageBundles, ...items] }),
    ...(data.bundleItems && { bundleItems: [...data.bundleItems, ...items] }),
  };
}

const pickRandom = <T>(items: T[], random: () => number) => items[Math.floor(random() * items.length)];

function shuffled<T>(items: T[], random: () => number) {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

// ponytail: bounded rejection sampling; a stubbed or stuck random falls back to a one-pick change after this many draws.
const MAX_REDRAWS = 50;

function redraw<T>(draw: () => T, same: (next: T) => boolean, patch: (next: T) => T) {
  for (let i = 0; i < MAX_REDRAWS; i++) {
    const next = draw();
    if (!same(next)) return next;
  }
  return patch(draw());
}

// R-RANDOM: fill only the empty picks; when all are filled, re-draw every pick so the result differs.
export function pickForMe(pkg: ByoPackage, current: Record<number, string> = {}, random = Math.random) {
  const grid = gridSlot(pkg);
  if (grid) {
    const k = grid.pickCount!;
    const ids = grid.options.map((o) => o.optionId);
    const kept = servedGridPicks(grid, current).slice(0, k);
    const unpicked = ids.filter((id) => !kept.includes(id));
    if (kept.length < k) return toPositions([...kept, ...shuffled(unpicked, random)].slice(0, k));
    if (ids.length <= k) return toPositions(kept);
    return toPositions(
      redraw(
        () => shuffled(ids, random).slice(0, k),
        (next) => next.every((id) => kept.includes(id)),
        (next) => {
          const out = pickRandom(next, random);
          const into = pickRandom(
            ids.filter((id) => !next.includes(id)),
            random,
          );
          return next.map((id) => (id === out ? into : id));
        },
      ),
    );
  }

  const open = pkg.slots;
  const valid = (s: ByoSlotConfig) => s.options.some((o) => o.optionId === current[s.slotIndex]);
  const empty = open.filter((s) => !valid(s));
  const keep = Object.fromEntries(open.filter(valid).map((s) => [s.slotIndex, current[s.slotIndex]]));
  const drawSlots = (slots: ByoSlotConfig[]): Record<number, string> => ({
    ...keep,
    ...Object.fromEntries(slots.map((s) => [s.slotIndex, pickRandom(s.options, random).optionId])),
  });
  if (empty.length) return drawSlots(empty);
  const changeable = open.filter((s) => s.options.length > 1);
  if (!changeable.length) return keep;
  return redraw(
    () => drawSlots(open),
    (next) => open.every((s) => next[s.slotIndex] === current[s.slotIndex]),
    (next) => {
      const slot = pickRandom(changeable, random);
      const others = slot.options.filter((o) => o.optionId !== current[slot.slotIndex]);
      return { ...next, [slot.slotIndex]: pickRandom(others, random).optionId };
    },
  );
}

export type ByoEventRow = ByoWidgetProps extends infer T
  ? T extends unknown
    ? Omit<T, keyof ByoEventIdentity>
    : never
  : never;

export const slotsToFill = (pkg: ByoPackage, picks: Record<number, string> = {}) => {
  const grid = gridSlot(pkg);
  if (grid) return grid.pickCount! - servedGridPicks(grid, picks).length;
  return pkg.slots.filter((s) => picks[s.slotIndex] === undefined).length;
};

export const byoSlots = (pkg: ByoPackage): ByoSlotProp[] => {
  const ids = (slot: ByoSlotConfig) => slot.options.map((o) => o.optionId);
  const grid = gridSlot(pkg);
  return grid
    ? [{ position: 1, option_ids: ids(grid) }]
    : pkg.slots.map((s) => ({ position: s.slotIndex + 1, option_ids: ids(s) }));
};

export const gridTilePosition = (slot: ByoSlotConfig, optionId?: string) =>
  slot.options.findIndex((o) => o.optionId === optionId) + 1;

export function byoOfferType(
  benefit: { benefitType: string; packages?: { packageId: number; price?: number; isBuyAll?: boolean }[] },
  packageId: number,
): ByoOfferType {
  const packages = benefit.packages ?? [];
  if (/gift/i.test(benefit.benefitType)) return 'gift';
  if (benefit.benefitType === 'rollingOffer') {
    return (packages.find((p) => p.packageId === packageId)?.price ?? 0) > 0 ? 'ro_paid' : 'ro_free';
  }
  return `${packages.filter((p) => !p.isBuyAll).length || 1}po` as ByoOfferType;
}

const BYO_ERROR_REASONS: Record<string, ByoErrorReason> = {
  unavailable: 'option_unavailable',
  not_in_slot: 'option_unavailable',
  missing: 'invalid_request',
};

export const byoErrorReason = (reason: string): ByoErrorReason => BYO_ERROR_REASONS[reason] ?? 'unknown';

export function byoRejectionOf(error: unknown): ByoRejection | null {
  const first = (error as { response?: { data?: { errors?: { errorCode?: number; fields?: ByoRejection }[] } } })
    ?.response?.data?.errors?.[0];
  return first?.errorCode === BYO_PICKS_INVALID_CODE && first.fields ? first.fields : null;
}
